import {
  getResourceDependencies,
  getResourceDependenciesWithHop
} from "api/ResourceDependenciesApi";

export const getResourceDependencySourceKey = ({
  tenantId,
  sourceId,
  sourceResourceType,
  direction
}) =>
  [tenantId, sourceResourceType, sourceId, direction].join(":");

export const getResourceDependencyContextId = (flowId, usageId) =>
  `${flowId}:${usageId}`;

export const getResourceDependencyQueryFromRow = (dependency) => ({
  tenantId: dependency.tenant_id,
  sourceId: dependency.source_id,
  sourceResourceType: dependency.source_resource_type,
  direction: dependency.direction,
  targetResourceType: dependency.target_resource_type
});

export const getFlowNamesUsingResource = async ({
  tenantId,
  sourceId,
  flows = []
}) => {
  const dependencyResponse = await getResourceDependencies({
    tenantId,
    sourceId,
    direction: "reverse",
    targetResourceType: "flow"
  });

  if (!dependencyResponse?.isSuccessful) {
    throw new Error(
      dependencyResponse?.message || "Retrieve resource dependencies failed"
    );
  }

  const flowIds = [
    ...new Set(
      (dependencyResponse.resultObject || []).map(
        (dependency) => dependency.target_id
      )
    )
  ];

  return flowIds
    .map((flowId) => flows.find((flow) => flow.id === flowId)?.name)
    .filter(Boolean);
};

export const getFlowNamesUsingCredential = async ({
  tenantId,
  credentialId,
  flows = []
}) => {
  const dependencyResponse = await getResourceDependenciesWithHop({
    tenantId,
    sourceId: credentialId,
    direction: "reverse",
    directTargetResourceType: "flow",
    throughTargetResourceType: "connector",
    finalTargetResourceType: "flow",
    targetIdFromContextId: true
  });

  if (!dependencyResponse?.isSuccessful) {
    throw new Error(
      dependencyResponse?.message || "Retrieve credential dependencies failed"
    );
  }

  const flowIds = [
    ...new Set(
      (dependencyResponse.resultObject || []).map(
        (dependency) => dependency.target_id
      )
    )
  ];

  return flowIds
    .map((flowId) => flows.find((flow) => flow.id === flowId)?.name)
    .filter(Boolean);
};

export const extractDependenciesFromFlowConfig = (flowId, flowConfig, stepsContext) => {
  const dependencies = [];
  const nodes = flowConfig?.nodes || [];

  nodes.forEach((node) => {
    if (node.type === "custom" && node.data?.componentId) {
      const connectorId = node.data.componentId;
      dependencies.push({
        sourceId: flowId,
        targetId: connectorId,
        sourceResourceType: "flow",
        targetResourceType: "connector",
        contextId: `${flowId}:${node.id}`
      });

      const credentialId = node.data.flowElement?.data?.credential?.value;
      if (credentialId) {
        dependencies.push({
          sourceId: connectorId,
          targetId: credentialId,
          sourceResourceType: "connector",
          targetResourceType: "credential",
          contextId: `${flowId}:${node.id}`
        });
      }
    }

    if (node.data?.type === "invokeComponent") {
      const subFlowId = node.data.flowElement?.data?.flowId?.value;
      if (subFlowId) {
        dependencies.push({
          sourceId: flowId,
          targetId: subFlowId,
          sourceResourceType: "flow",
          targetResourceType: "flow",
          contextId: `${flowId}:${node.id}`
        });
      }
    }

    if (node.type !== "custom" && node.data?.type !== "invokeComponent" && node.type !== "subflow") {
      const credentialId = node.data.flowElement?.data?.credential?.value;
      if (credentialId) {
        dependencies.push({
          sourceId: flowId,
          targetId: credentialId,
          sourceResourceType: "flow",
          targetResourceType: "credential",
          contextId: `${flowId}:${node.id}`
        });
      }
    }
  });

  const triggers = flowConfig?.triggers || [];
  const contextTriggers = stepsContext?.triggers || {};
  triggers.forEach((trigger) => {
    const triggerType = trigger.type;
    const contextKey =
      triggerType === "restEndpointTrigger" ? "rest" :
      triggerType === "webhookTrigger"      ? "webhook" : null;
    const credentialId =
      (contextKey && contextTriggers[contextKey]?.credentials?.value) ||
      trigger.properties?.credentials?.value;
    if (credentialId) {
      dependencies.push({
        sourceId: flowId,
        targetId: credentialId,
        sourceResourceType: "flow",
        targetResourceType: "credential",
        contextId: `${flowId}:${trigger.type}`
      });
    }
  });


  return dependencies;
};

export const translateDependency = (dep, originalFlowId, newFlowId) => {
  const sourceId = dep.source_id === originalFlowId ? newFlowId : dep.source_id;
  const targetId = dep.target_id === originalFlowId ? newFlowId : dep.target_id;
  let contextId = dep.context_id;
  if (contextId === originalFlowId) {
     contextId = newFlowId;
  } else if (typeof contextId === "string" && contextId.includes(originalFlowId)) {
     contextId = contextId.replace(originalFlowId, newFlowId);
  }
  return {
     sourceId,
     targetId,
     sourceResourceType: dep.source_resource_type,
     targetResourceType: dep.target_resource_type,
     contextId
  };
};

export const getCopiedFlowDependencies = async ({
  tenantId,
  originalFlowId,
  newFlowId
}) => {
  const flowDependenciesResponse = await getResourceDependencies({
    tenantId,
    sourceId: originalFlowId,
    direction: "forward"
  });
  const flowDependencies = Array.isArray(flowDependenciesResponse?.resultObject)
    ? flowDependenciesResponse.resultObject
    : [];
  const copiedDependencies = flowDependencies.map((dependency) =>
    translateDependency(dependency, originalFlowId, newFlowId)
  );
  const connectorIds = [
    ...new Set(
      flowDependencies
        .filter(
          (dependency) => dependency.target_resource_type === "connector"
        )
        .map((dependency) => dependency.target_id)
    )
  ];

  for (const connectorId of connectorIds) {
    const connectorDependenciesResponse = await getResourceDependencies({
      tenantId,
      sourceId: connectorId,
      direction: "forward"
    });
    const connectorDependencies = Array.isArray(
      connectorDependenciesResponse?.resultObject
    )
      ? connectorDependenciesResponse.resultObject
      : [];

    connectorDependencies
      .filter(
        (dependency) =>
          dependency.context_id === originalFlowId ||
          dependency.context_id?.startsWith(`${originalFlowId}:`)
      )
      .forEach((dependency) => {
        copiedDependencies.push(
          translateDependency(dependency, originalFlowId, newFlowId)
        );
      });
  }

  return copiedDependencies;
};
